import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import {
  createEmptyMasterForm,
  type CounterpartyMasterForm,
} from '@/modules/counterparty/counterparty-master/types/master-form.ts';
import { useUserStore } from '@/modules/core/stores/user.store.ts';
import { clearPendingDirectory } from '@/modules/counterparty/counterparty-master/utils/pending-directory.util.ts';
import { createWizardNestedSession, type WizardNestedRunOptions } from '@/wizard/nested-session.ts';

export const useCounterpartyMasterStore = defineStore(StoreId.CounterpartyMaster, {
  state: () => ({
    isOpen: false,
  }),
  actions: {
    open() {
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
    },
  },
});

const formData = ref<CounterpartyMasterForm>(createEmptyMasterForm());
const createdId = ref<number | null>(null);
const completedTaskCodes = new Set<string>();
const submittedItemIds = new Set<string>();
const nestedSession = createWizardNestedSession<number>();

function prepareMaster(prefill?: { name?: string }) {
  resetCounterpartyMasterData();

  if (prefill?.name) {
    formData.value = { ...formData.value, name: prefill.name };
  }
}

export function useCounterpartyMaster() {
  const store = useCounterpartyMasterStore();
  const { isOpen } = storeToRefs(store);

  return {
    isOpen,
    formData,
    createdId,
    completedTaskCodes,
    submittedItemIds,
    isNested: nestedSession.isNested,
    open: () => {
      nestedSession.cancel();
      prepareMaster();
      store.open();
    },
    openNested: (prefill?: { name?: string }, options?: WizardNestedRunOptions) =>
      nestedSession.runNested(() => {
        prepareMaster(prefill);
        store.open();
      }, options),
    close: () => store.close(),
    setManagerUser,
    setCreatedId,
  };
}

export function setCreatedId(id: number) {
  createdId.value = id;
}

export function setManagerUser() {
  const userStore = useUserStore();

  if (!userStore.isDefined) {
    return;
  }

  formData.value = {
    ...formData.value,
    manager: {
      code: userStore.required.id,
      label: userStore.required.name,
    },
  };
}

export function resetCounterpartyMasterData() {
  formData.value = createEmptyMasterForm();
  createdId.value = null;
  completedTaskCodes.clear();
  submittedItemIds.clear();
  clearPendingDirectory();
}

export function settleCounterpartyMasterNested() {
  const id = createdId.value;
  resetCounterpartyMasterData();
  nestedSession.settle(id);
}

export function resetCounterpartyMaster() {
  nestedSession.cancel();
  resetCounterpartyMasterData();
  useCounterpartyMasterStore().close();
}

export function useCounterpartyMasterDirty(page: Ref<number>) {
  return computed(() => {
    const data = formData.value;
    return (
      page.value > 0 ||
      Boolean(data.name) ||
      Boolean(data.code) ||
      Boolean(data.inn) ||
      Boolean(data.legalForm) ||
      Boolean(data.kpp) ||
      Boolean(data.ogrn) ||
      Boolean(data.okpo) ||
      Boolean(data.okato) ||
      Boolean(data.segment) ||
      Boolean(data.region) ||
      Boolean(data.city) ||
      Object.values(data.sections).some(Boolean) ||
      data.addresses.length > 0 ||
      data.contacts.length > 0 ||
      data.bankDetails.length > 0 ||
      data.isSubordinated ||
      data.parents.length > 0 ||
      Boolean(data.comment)
    );
  });
}
